import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker"; // 🔥 NEW
import "react-datepicker/dist/react-datepicker.css"; // 🔥 NEW
import Filters from "./components/Filters";
import Leaderboard from "./components/Leaderboard";
import DeveloperPieChart from "./components/DeveloperPieChart";
// import BugList from "./components/BugList";
import { fetchLeaderboard } from "./services/api";
import "./App.css"; 

const parseTimestamp = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return null;
    }

    const numericValue = Number(trimmedValue);
    if (Number.isFinite(numericValue)) {
      return numericValue < 1e12 ? numericValue * 1000 : numericValue;
    }

    const parsedValue = Date.parse(trimmedValue);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  return null;
};

function ExistingFlow() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
    
  // 🔥 NEW: State to track our time filter
  const [timeframe, setTimeframe] = useState("all");
  // Add these two lines:
  const [startDate, setStartDate] = useState(null); 
  const [endDate, setEndDate] = useState(null);

  const handleSelect = async (projectId, sprintId) => {
    setIsDashboardLoading(true);
    try {
      // Reset the time filter whenever a new sprint is loaded
      setStartDate(null);
      setEndDate(null);
      setTimeframe("all");
      const result = await fetchLeaderboard(projectId, sprintId);
      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setIsDashboardLoading(false);
    }
  };

  // 1. Safely extract the raw bug list from the backend
  const baseBugList = useMemo(
    () => (Array.isArray(data?.bugList) ? data.bugList : []),
    [data]
  );


  const filteredBugs = useMemo(() => {
  if (!baseBugList.length) return [];
  if (timeframe === "all") return baseBugList;

  const now = new Date();
  const day = now.getDay();
  const adjustedDay = day === 0 ? 7 : day;

  // THIS WEEK
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - (adjustedDay - 1));
  startOfWeek.setHours(0, 0, 0, 0);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 4);
  endOfWeek.setHours(23, 59, 59, 999);

  // LAST WEEK (Previous Mon–Fri)
  const startOfLastWeek = new Date(startOfWeek);
  startOfLastWeek.setDate(startOfWeek.getDate() - 7);

  const endOfLastWeek = new Date(startOfLastWeek);
  endOfLastWeek.setDate(startOfLastWeek.getDate() + 4);
  endOfLastWeek.setHours(23, 59, 59, 999);

  // THIS MONTH
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  startOfMonth.setHours(0, 0, 0, 0);

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const oneDay = 24 * 60 * 60 * 1000;

  return baseBugList.filter(bug => {
    const time = parseTimestamp(bug.createdAt);
    if (time === null) return false;

    switch (timeframe) {
      case "today":
        return time >= startOfToday;

      case "yesterday":
        return time >= startOfToday - oneDay && time < startOfToday;

      case "thisWeek":
        return time >= startOfWeek.getTime() && time <= endOfWeek.getTime();

      case "lastWeek":
        return time >= startOfLastWeek.getTime() && time <= endOfLastWeek.getTime();

      case "thisMonth":
        return time >= startOfMonth.getTime() && time <= now.getTime();

      case "custom":
        if (!startDate || !endDate) return true; // Show all until both are selected
        
        // Clone the Date objects provided by the picker
        const customStart = new Date(startDate.getTime());
        customStart.setHours(0, 0, 0, 0);
        
        const customEnd = new Date(endDate.getTime());
        customEnd.setHours(23, 59, 59, 999);
        
        return time >= customStart.getTime() && time <= customEnd.getTime();

      default:
        return true;
    }
  });
}, [baseBugList, timeframe, startDate, endDate]);

  // 3. 🔥 THE DYNAMIC AGGREGATOR: Rebuilds charts based on the filtered bugs!
  const dynamicMetrics = useMemo(() => {
    const devCounts = {};
    const epicCounts = {};

    filteredBugs.forEach(bug => {
      // Recount for Developers
      const dev = bug.culprit || "Unknown/External";
      devCounts[dev] = (devCounts[dev] || 0) + 1;

      // Recount for Epics
      const epic = bug.epic || "No Epic";
      epicCounts[epic] = (epicCounts[epic] || 0) + 1;
    });

    const format = (obj) =>
      Object.entries(obj).map(([name, count]) => ({ name, count }));

    return {
      totalBugs: filteredBugs.length,
      developerLeaderboard: format(devCounts),
      epicLeaderboard: format(epicCounts)
    };
  }, [filteredBugs]);

  // 4. Map our dynamic metrics to the variables the UI expects
  const developerLeaderboard = dynamicMetrics.developerLeaderboard;
  const epicLeaderboard = dynamicMetrics.epicLeaderboard;
  const totalBugs = dynamicMetrics.totalBugs;

  // Find the top performer (Lowest bugs)
  const sortedLeaderboard = [...developerLeaderboard].sort(
    (a, b) => a.count - b.count 
  );
  const topPerformer = sortedLeaderboard[0];
  const activeDevelopers = sortedLeaderboard.length;

  return (
    <div className="dashboard-shell">
      <div className="dashboard-aura aura-one" />
      <div className="dashboard-aura aura-two" />

      <div className="dashboard-container">
        <button
          type="button"
          className="dashboard-back-button"
          onClick={() => navigate("/")}
        >
          <span aria-hidden="true">←</span>
          <span>Back to Landing</span>
        </button>

        <section className="dashboard-header card">
          <div className="dashboard-header-copy">
            <p className="eyebrow">Zoho Sprint Intelligence</p>
            <h1>Bug Analytics Dashboard</h1>
            <p className="dashboard-description">
              Transform raw sprint defects into a sharper command center for
              productivity, ownership, and bug volume trends.
            </p>
          </div>

          <div className="header-stat-cluster">
            <div className="header-stat">
              <span className="header-stat-label">Focus</span>
              <strong>Leaderboard clarity</strong>
              <p>See contribution patterns instantly.</p>
            </div>
            <div className="header-stat accent-stat">
              <span className="header-stat-label">View</span>
              <strong>Sprint-level bug share</strong>
              <p>Compare counts and distribution at a glance.</p>
            </div>
          </div>
        </section>

        <section className="control-deck card">
          <div className="control-copy">
            <p className="section-kicker">Launch Analysis</p>
            <h2>Choose a project and sprint to generate the board</h2>
            <p>
              The interaction flow stays the same. This layer is tuned purely
              for a more immersive visual experience.
            </p>
          </div>

          <Filters onSelect={handleSelect} isSubmitting={isDashboardLoading} />
        </section>

        {!data && (
          <section className="empty-state card">
            <div className="empty-state-badge">Ready for lift-off</div>
            <h2>Select a sprint to reveal the dashboard</h2>
            <p>
              Your data pipeline is already working. Once you load a sprint,
              the leaderboard, total bug count, and developer share visual will
              appear in this redesigned command center.
            </p>
          </section>
        )}

        {data && (
          <div className="dashboard-content" style={{ position: 'relative' }}>
            
            {/* 🔥 NEW TIMEFRAME FILTER BAR */}
            <div className="card" style={{ padding: '16px 28px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', position: 'relative' }}>
                      <span className="filter-label" style={{ margin: 0 }}>Timeframe:</span>
              <select
                className="select-input"
                style={{ minHeight: '40px', width: 'auto', padding: '0 16px', fontSize: '0.9rem' }}
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
              >
                <option value="all">All Sprint Bugs</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="thisWeek">This Week [Mon - Fri]</option>
                <option value="lastWeek">Past Week [Mon - Fri]</option>
                <option value="thisMonth">This Month</option>
                <option value="custom">Custom Range</option> {/* Added Option */}
              </select>



              {/* Conditional rendering for Custom Date Pickers */}
              {timeframe === "custom" && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    dateFormat="dd/MM/yyyy"
                    className="select-input"
                    placeholderText="DD/MM/YYYY"
                    portalId="root-portal" /* 🔥 TELEPORTS CALENDAR ABOVE EVERYTHING */
                  />
                  <span style={{ fontSize: '0.9rem', color: '#666' }}>to</span>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    dateFormat="dd/MM/yyyy"
                    className="select-input"
                    placeholderText="DD/MM/YYYY"
                    portalId="root-portal" /* 🔥 TELEPORTS CALENDAR ABOVE EVERYTHING */
                  />
                </div>
              )}

              <span className="section-muted" style={{ marginLeft: 'auto', fontSize: '0.9rem' }}>
                Showing {totalBugs} bugs
              </span>
            </div>


            <div className="dashboard-grid">

              {/* 🔹 TOP: KPI ROW */}
              <div className="kpi-row">
                <div className="metric-widget card">
                  <p className="metric-title">Total Bugs Found</p>
                  <p className="metric-value">{totalBugs}</p>
                  <p className="metric-footnote">
                    Captured for the selected timeframe.
                  </p>
                </div>

                <div className="insight-card card">
                  <div className="insight-pill">Sprint Pulse</div>
                  <div className="insight-grid">
                    <div>
                      <span className="insight-label">Developer with lowest bug count</span>
                      <strong>{topPerformer ? topPerformer.name : "N/A"}</strong>
                    </div>
                    <div>
                      <span className="insight-label">Active developers</span>
                      <strong>{activeDevelopers}</strong>
                    </div>
                  </div>
                </div>
              </div>


                {/* 🔹 MIDDLE: CHARTS */}
              <div className="charts-row">
                <div className="card chart-card">
                  <div className="chart-card-header">
                    <h3>Developer Bug Share</h3>
                  </div>
                  <DeveloperPieChart data={developerLeaderboard} />
                </div>

                <div className="card chart-card">
                  <div className="chart-card-header">
                    <h3>Epic Bug Distribution</h3>
                  </div>
                  <DeveloperPieChart data={epicLeaderboard} />
                </div>
              </div>

                {/* 🔹 BOTTOM: LEADERBOARDS */}
              <div className="leaderboard-row">
                <Leaderboard
                  data={developerLeaderboard}
                  sortDirection="asc"
                />

                <Leaderboard
                  title="Epic Leaderboard"
                  subtitle="Bug count grouped by epic"
                  data={epicLeaderboard}
                  variant="epic"
                />
              </div>

            </div>

            {/* <BugList data={filteredBugs} /> */}
          </div>
        )}
      </div>
    </div>
  );
}

export default ExistingFlow;

// import React, { useState, useMemo } from "react";
// import Filters from "./components/Filters";
// import Leaderboard from "./components/Leaderboard";
// import DeveloperPieChart from "./components/DeveloperPieChart";
// import { fetchSummary } from "./services/api";
// import "./App.css";

// const parseTimestamp = (value) => {
//   if (!value) return null;

//   if (typeof value === "number") return value;

//   const parsed = Date.parse(value);
//   return isNaN(parsed) ? null : parsed;
// };

// function ExistingFlow() {
//   const [data, setData] = useState(null);
//   const [timeframe, setTimeframe] = useState("all");

//   const handleSelect = async (projectId) => {
//     try {
//       setTimeframe("all");
//       const result = await fetchSummary(projectId);
//       setData(result);
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   // ✅ BASE DATA
//   const baseList = useMemo(
//     () => (Array.isArray(data) ? data : []),
//     [data]
//   );

//   // ✅ TIME FILTER (based on completedAt)
//   const filteredData = useMemo(() => {
//     if (!baseList.length) return [];
//     if (timeframe === "all") return baseList;

//     const now = new Date();
//     const day = now.getDay();
//     const adjustedDay = day === 0 ? 7 : day;

//     // THIS WEEK (Mon–Fri)
//     const startOfWeek = new Date(now);
//     startOfWeek.setDate(now.getDate() - (adjustedDay - 1));
//     startOfWeek.setHours(0, 0, 0, 0);

//     const endOfWeek = new Date(startOfWeek);
//     endOfWeek.setDate(startOfWeek.getDate() + 4);
//     endOfWeek.setHours(23, 59, 59, 999);

//     // LAST WEEK
//     const startOfLastWeek = new Date(startOfWeek);
//     startOfLastWeek.setDate(startOfWeek.getDate() - 7);

//     const endOfLastWeek = new Date(startOfLastWeek);
//     endOfLastWeek.setDate(startOfLastWeek.getDate() + 4);
//     endOfLastWeek.setHours(23, 59, 59, 999);

//     // TODAY
//     const startOfToday = new Date(
//       now.getFullYear(),
//       now.getMonth(),
//       now.getDate()
//     ).getTime();

//     const oneDay = 24 * 60 * 60 * 1000;

//     return baseList.filter(item => {
//       const time = parseTimestamp(item.completedAt);
//       if (!time) return false;

//       switch (timeframe) {
//         case "today":
//           return time >= startOfToday;

//         case "yesterday":
//           return time >= startOfToday - oneDay && time < startOfToday;

//         case "thisWeek":
//           return time >= startOfWeek.getTime() && time <= endOfWeek.getTime();

//         case "lastWeek":
//           return time >= startOfLastWeek.getTime() && time <= endOfLastWeek.getTime();

//         default:
//           return true;
//       }
//     });
//   }, [baseList, timeframe]);

//   // ✅ DYNAMIC AGGREGATION
//   const dynamicMetrics = useMemo(() => {
//     const devCounts = {};
//     const typeCounts = {};

//     filteredData.forEach(item => {
//       const dev = item.completedBy || "Unknown";
//       devCounts[dev] = (devCounts[dev] || 0) + 1;

//       const type = item.type || "other";
//       typeCounts[type] = (typeCounts[type] || 0) + 1;
//     });

//     const format = (obj) =>
//       Object.entries(obj).map(([name, count]) => ({ name, count }));

//     return {
//       totalItems: filteredData.length,
//       developerLeaderboard: format(devCounts),
//       typeDistribution: format(typeCounts)
//     };
//   }, [filteredData]);

//   const developerLeaderboard = dynamicMetrics.developerLeaderboard;
//   const typeDistribution = dynamicMetrics.typeDistribution;
//   const totalItems = dynamicMetrics.totalItems;

//   const sortedLeaderboard = [...developerLeaderboard].sort(
//     (a, b) => b.count - a.count
//   );

//   const topPerformer = sortedLeaderboard[0];
//   const activeDevelopers = sortedLeaderboard.length;

//   return (
//     <div className="dashboard-shell">
//       <div className="dashboard-container">

//         {/* HEADER */}
//         <section className="dashboard-header card">
//           <h1>Developer Productivity Dashboard</h1>
//           <p>Track completed tasks across timeframes</p>
//         </section>

//         {/* FILTER */}
//         <section className="control-deck card">
//           <Filters onSelect={handleSelect} />
//         </section>

//         {!data && (
//           <section className="empty-state card">
//             <h2>Select a project to view analytics</h2>
//           </section>
//         )}

//         {data && (
//           <div className="dashboard-content">

//             {/* TIME FILTER */}
//             <div className="card" style={{ padding: 16, marginBottom: 20 }}>
//               <select
//                 value={timeframe}
//                 onChange={(e) => setTimeframe(e.target.value)}
//               >
//                 <option value="all">All Data</option>
//                 <option value="today">Today</option>
//                 <option value="yesterday">Yesterday</option>
//                 <option value="thisWeek">This Week</option>
//                 <option value="lastWeek">Last Week</option>
//               </select>

//               <span style={{ marginLeft: 20 }}>
//                 Showing {totalItems} items
//               </span>
//             </div>

//             {/* KPI */}
//             <div className="kpi-row">
//               <div className="card">
//                 <h3>Total Completed</h3>
//                 <h1>{totalItems}</h1>
//               </div>

//               <div className="card">
//                 <h3>Top Performer</h3>
//                 <h2>{topPerformer?.name || "N/A"}</h2>
//               </div>
//             </div>

//             {/* CHARTS */}
//             <div className="charts-row">
//               <div className="card">
//                 <h3>Developer Contribution</h3>
//                 <DeveloperPieChart data={developerLeaderboard} />
//               </div>

//               <div className="card">
//                 <h3>Type Distribution</h3>
//                 <DeveloperPieChart data={typeDistribution} />
//               </div>
//             </div>

//             {/* LEADERBOARD */}
//             <div className="leaderboard-row">
//               <Leaderboard data={developerLeaderboard} />
//             </div>

//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// export default ExistingFlow;
